import { Config } from '../config';
import { EventTypeIdentifier } from '../enums';
import { CreateFleet, DeviceHealth, EventType, Fleet, FleetEventTypePayload, LicensePlateType, UpdateFleet } from '../interfaces';
import { AuthService } from './auth.service';
import { HttpService } from './http.service';

const path = `${Config.API}/fleets`;

const displayId = (item: EventType) => {
  // wanted order

  // Bus Lane - Parking Match
  // Bus Lane - Moving Match
  // Bus Lane - Match
  // Bus Stop - Match
  // DOUBLE_PARKED_MATCH
  if (item.identifier === EventTypeIdentifier.BUS_LANE_PARKING_MATCH) {
    return 1;
  }
  if (item.identifier === EventTypeIdentifier.BUS_LANE_MOVING_MATCH) {
    return 2;
  }
  if (item.identifier === EventTypeIdentifier.BUS_LANE_MATCH) {
    return 3;
  }
  if (item.identifier === EventTypeIdentifier.BUS_STOP_MATCH) {
    return 4;
  }
  if (item.identifier === EventTypeIdentifier.DOUBLE_PARKED_MATCH) {
    return 5;
  }
  return undefined;
};

const eventTypesSorting = (array: EventType[], withDisplayId = false) => {
  if (!withDisplayId) {
    return array.sort((a, b) => a.identifier.localeCompare(b.identifier));
  }

  return array
    .map((item: EventType) => ({
      ...item,
      displayId: displayId(item),
    }))
    .sort((a, b) => a.identifier.localeCompare(b.identifier))
    .sort((a, b) => {
      if (a.displayId === undefined) return 1;
      if (b.displayId === undefined) return -1;
      return a.displayId > b.displayId ? 1 : -1;
    });
};

export abstract class FleetService {
  public static getAll = (): Promise<Fleet[]> => {
    return HttpService.fetchAuthorized(path).then((res) => {
      return res || [];
    });
  };

  public static getHealthConfigFilters = (): Promise<string[]> => {
    const url = `${Config.API}/alert_configs/filters`;
    return HttpService.fetchAuthorized(url).then((res) => {
      const filters = res.filter_period ?? [];
      filters.sort((a: string, b: string) => {
        return a.localeCompare(b);
      });

      return filters;
    });
  };

  public static getHealthConfig = (): Promise<any> => {
    const url = `${Config.API}/alert_configs`;
    return HttpService.fetchAuthorized(url).then((res) => res);
  };

  public static getHealth = (checkPeriod?: number): Promise<DeviceHealth[]> => {
    let url = `${Config.APIv2}/devices_health`;
    if (checkPeriod) {
      url += `?check_period=${checkPeriod}h`;
    }

    return HttpService.fetchAuthorized(url).then((res) => {
      return res || [];
    });
  };

  public static requestHealthReportCSV = (internal = false): Promise<{ Report: string }> => {
    let url = `${Config.API}/reports/csv/fleethealth`;

    if (internal) {
      url += '?group_scope=internal';
    }

    return HttpService.fetchAuthorized(url, { method: 'POST' }).then((res) => res);
  };

  public static update = (uuid: string, body: UpdateFleet): Promise<any> => {
    return HttpService.fetchAuthorized(`${path}/${uuid}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }).then((res) => res);
  };

  public static create = (body: CreateFleet): Promise<any> => {
    return HttpService.fetchAuthorized(path, {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((res) => res);
  };

  public static uploadFleetLogo = (uuid: string, body: FormData): Promise<any> => {
    return HttpService.fetchAuthorized(`${path}/${uuid}/logo`, {
      method: 'POST',
      body,
    }).then(
      (res) => Promise.resolve(res),
      (rej) => Promise.reject(rej),
    );
  };

  // We allow a 401 here to not log out people who belong to NO_ACCESS group
  public static get = (uuid: string): Promise<Fleet> => {
    return HttpService.fetchAuthorized(`${path}/${uuid}`, undefined, undefined, true).then(
      (res) => {
        return res;
      },
      (rej) => {
        if (rej.status === 401) {
          return Promise.resolve({});
        }
        return rej;
      },
    );
  };

  public static getMyFleet = async (): Promise<Fleet> => {
    try {
      const fleetId = await AuthService.getUserFleetId();
      if (fleetId !== null) {
        return FleetService.get(fleetId);
      }
      return Promise.reject();
    } catch (error) {
      console.error(error);
      return Promise.reject();
    }
  };

  public static getAllFleetsEventTypes = (): Promise<EventType[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/fleets/event_types`, {
      method: 'GET',
    }).then((eventTypesFromResponse) => {
      return eventTypesSorting(eventTypesFromResponse) || [];
    });
  };

  public static getFleetEventTypes = (fleetUuid: string): Promise<EventType[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/fleets/${fleetUuid}/event_types`, {
      method: 'GET',
    }).then((eventTypesFromResponse) => {
      return eventTypesSorting(eventTypesFromResponse, true) || [];
    });
  };

  public static createFleetEventTypes = (fleetUuid: string, fleetEventTypesPayload: FleetEventTypePayload): Promise<EventType[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/fleets/${fleetUuid}/event_types`, {
      method: 'POST',
      body: JSON.stringify(fleetEventTypesPayload),
    }).then((eventTypesFromResponse) => {
      return eventTypesFromResponse || [];
    });
  };

  public static updateFleetEventTypes = (
    fleetUuid: string,
    eventTypeId: number,
    fleetEventTypesPayload: FleetEventTypePayload,
  ): Promise<EventType[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/fleets/${fleetUuid}/event_types/${eventTypeId}`, {
      method: 'PUT',
      body: JSON.stringify(fleetEventTypesPayload),
    }).then((eventTypesFromResponse) => {
      return eventTypesFromResponse || [];
    });
  };

  public static deleteFleetEventTypes = (fleetUuid: string, eventTypeId: number): Promise<EventType[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/fleets/${fleetUuid}/event_types/${eventTypeId}`, {
      method: 'DELETE',
    }).then((eventTypesFromResponse) => {
      return eventTypesFromResponse || [];
    });
  };

  public static getLicensePlateTypes = (fleetUuid: string): Promise<LicensePlateType[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/fleets/${fleetUuid}/license_plate_types`, {
      method: 'GET',
    }).then((res) => {
      return res || [];
    });
  };

  public static getBlockNames = (fleetUuid: string, withDirections = false): Promise<string[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/fleets/${fleetUuid}/block_names?with_directions=${withDirections}`, {
      method: 'GET',
    }).then((res) => {
      return res || [];
    });
  };

  public static getFullFleetData = (fleetUuid: string): Promise<Fleet> => {
    return HttpService.fetchAuthorized(`${path}/${fleetUuid}`, {
      cache: 'no-store',
    }).then((res) => res);
  };
}
